import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/Layout';
import {
  settingsAPI,
  storageAPI,
  usersAPI,
  AppSettings,
  StorageStatsResponse,
  AuditResult,
  StorageFile,
  User,
} from '../api/client';
import {
  useAuth,
  usePermission,
  getRoleLabel,
  getRoleColor,
} from '../context/AuthContext';
import UserModal from '../components/UserModal';
import './Settings.css';

/**
 * Time options for reminder scheduling (EST)
 */
const TIME_OPTIONS = [
  { value: '06:00', label: '6:00 AM' },
  { value: '07:00', label: '7:00 AM' },
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
];

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Storage state
  const [storageStats, setStorageStats] = useState<StorageStatsResponse | null>(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [deletingOrphans, setDeletingOrphans] = useState(false);

  // Bucket reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resettingBucket, setResettingBucket] = useState(false);

  // File browser modal state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [fileBrowserSource, setFileBrowserSource] = useState<'s3' | 'local'>('s3');
  const [storageFiles, setStorageFiles] = useState<StorageFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // Local reset modal state
  const [showLocalResetModal, setShowLocalResetModal] = useState(false);
  const [localResetConfirmation, setLocalResetConfirmation] = useState('');
  const [resettingLocal, setResettingLocal] = useState(false);

  // User management state
  const { user: currentUser } = useAuth();
  const canViewUsers = usePermission('users.view');
  const canCreateUsers = usePermission('users.create');
  const canEditUsers = usePermission('users.edit');
  const canDeleteUsers = usePermission('users.delete');
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState<AppSettings>({
    default_expiration_days: 7,
    default_notify_admin: true,
    default_max_revisions: 1,
    reminder_enabled: true,
    reminder_days_before: 3,
    reminder_time: '09:00',
    admin_notification_email: '',
    business_name: 'Seraphina Grey',
    support_email: 'hello@seraphinagrey.com',
  });

  // Load settings and users on mount
  useEffect(() => {
    loadSettings();
    if (canViewUsers) {
      loadUsers();
    }
  }, [canViewUsers]);

  const loadUsers = async () => {
    if (!canViewUsers) return;
    try {
      setUsersLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.users);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleUserSaved = (_savedUser: User) => {
    setShowUserModal(false);
    setEditingUser(null);
    // Refresh user list
    loadUsers();
    setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
  };

  const handleDeleteUser = async (userId: number) => {
    const userToDelete = users.find((u) => u.id === userId);
    if (!userToDelete) return;

    if (userToDelete.username === 'supervisor') {
      setError('Cannot delete supervisor user');
      return;
    }

    try {
      setDeletingUserId(userId);
      await usersAPI.delete(userId);
      setSuccess('User deactivated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleReactivateUser = async (userId: number) => {
    try {
      await usersAPI.reactivate(userId);
      setSuccess('User reactivated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reactivate user');
    }
  };

  const formatLastLogin = (lastLogin: string | null): string => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsAPI.get();
      setSettings(response.settings);
      setFormData(response.settings);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) : value,
    }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await settingsAPI.update(formData);

      setSuccess('Settings saved successfully');
      setHasChanges(false);
      setSettings(formData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData(settings);
      setHasChanges(false);
      setSuccess(null);
    }
  };

  // Storage functions
  const loadStorageStats = async () => {
    try {
      setStorageLoading(true);
      const response = await storageAPI.getStats();
      setStorageStats(response);
    } catch (err: any) {
      console.error('Failed to load storage stats:', err);
    } finally {
      setStorageLoading(false);
    }
  };

  const runStorageAudit = async () => {
    try {
      setAuditLoading(true);
      setAuditResult(null);
      const response = await storageAPI.runAudit();
      setAuditResult(response.audit);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run storage audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const deleteOrphanedFiles = async () => {
    if (!auditResult || auditResult.orphanedFiles.length === 0) return;

    try {
      setDeletingOrphans(true);
      const keys = auditResult.orphanedFiles.map((f) => f.key);
      const response = await storageAPI.deleteOrphans(keys);
      setSuccess(`Deleted ${response.deleted} orphaned files`);
      setAuditResult(null);
      loadStorageStats(); // Refresh stats
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete orphaned files');
    } finally {
      setDeletingOrphans(false);
    }
  };

  const handleResetBucket = async () => {
    if (!storageStats?.s3.bucketName) return;

    try {
      setResettingBucket(true);
      setError(null);
      const response = await storageAPI.resetBucket(resetConfirmation);
      setSuccess(response.message);
      setShowResetModal(false);
      setResetConfirmation('');
      setAuditResult(null);
      loadStorageStats(); // Refresh stats
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset bucket');
    } finally {
      setResettingBucket(false);
    }
  };

  // File browser functions
  const openFileBrowser = async (source: 's3' | 'local') => {
    setFileBrowserSource(source);
    setShowFileBrowser(true);
    setStorageFiles([]);
    setFilesLoading(true);

    try {
      const response = await storageAPI.getFiles(source);
      setStorageFiles(response.files);
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to load ${source} files`);
    } finally {
      setFilesLoading(false);
    }
  };

  // Local reset functions
  const handleResetLocal = async () => {
    try {
      setResettingLocal(true);
      setError(null);
      const response = await storageAPI.resetLocalStorage(localResetConfirmation);
      setSuccess(response.message);
      setShowLocalResetModal(false);
      setLocalResetConfirmation('');
      loadStorageStats(); // Refresh stats
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset local storage');
    } finally {
      setResettingLocal(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="settings-page">
          <div className="settings-loading">
            <div className="loading-spinner" />
            <p>Loading settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="settings-page">
        <div className="settings-header">
          <h1 className="settings-title">Settings</h1>
          <p className="settings-subtitle">
            Configure global defaults and notification preferences
          </p>
        </div>

        {error && (
          <div className="settings-alert settings-alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="settings-alert settings-alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="settings-form">
          {/* Session Defaults */}
          <section className="settings-section">
            <h2 className="settings-section-title">Session Defaults</h2>
            <p className="settings-section-description">
              Default values applied to new proof sessions
            </p>

            <div className="settings-field">
              <label htmlFor="default_expiration_days" className="settings-label">
                Default Expiration
              </label>
              <div className="settings-input-group">
                <input
                  type="number"
                  id="default_expiration_days"
                  name="default_expiration_days"
                  value={formData.default_expiration_days}
                  onChange={handleChange}
                  min={1}
                  max={365}
                  className="settings-input settings-input-small"
                />
                <span className="settings-input-suffix">days</span>
              </div>
              <p className="settings-help">
                Sessions expire after this many days from creation
              </p>
            </div>

            <div className="settings-field settings-field-checkbox">
              <label className="settings-checkbox-label">
                <input
                  type="checkbox"
                  name="default_notify_admin"
                  checked={formData.default_notify_admin}
                  onChange={handleChange}
                  className="settings-checkbox"
                />
                <span className="settings-checkbox-text">
                  Notify admin when customer completes review
                </span>
              </label>
              <p className="settings-help">
                Can be overridden per session
              </p>
            </div>

            <div className="settings-field">
              <label htmlFor="default_max_revisions" className="settings-label">
                Default Revision Limit
              </label>
              <div className="settings-input-group">
                <input
                  type="number"
                  id="default_max_revisions"
                  name="default_max_revisions"
                  value={formData.default_max_revisions}
                  onChange={handleChange}
                  min={1}
                  max={10}
                  className="settings-input settings-input-small"
                />
                <span className="settings-input-suffix">revision{formData.default_max_revisions !== 1 ? 's' : ''} per photo</span>
              </div>
              <p className="settings-help">
                Maximum times a customer can request changes per photo. Can be overridden per session.
              </p>
            </div>
          </section>

          {/* Expiration Reminders */}
          <section className="settings-section">
            <h2 className="settings-section-title">Expiration Reminders</h2>
            <p className="settings-section-description">
              Automatic email reminders before sessions expire
            </p>

            <div className="settings-field settings-field-checkbox">
              <label className="settings-checkbox-label">
                <input
                  type="checkbox"
                  name="reminder_enabled"
                  checked={formData.reminder_enabled}
                  onChange={handleChange}
                  className="settings-checkbox"
                />
                <span className="settings-checkbox-text">
                  Send reminder emails before session expires
                </span>
              </label>
            </div>

            {formData.reminder_enabled && (
              <>
                <div className="settings-field">
                  <label htmlFor="reminder_days_before" className="settings-label">
                    Send Reminder
                  </label>
                  <div className="settings-input-group">
                    <input
                      type="number"
                      id="reminder_days_before"
                      name="reminder_days_before"
                      value={formData.reminder_days_before}
                      onChange={handleChange}
                      min={1}
                      max={30}
                      className="settings-input settings-input-small"
                    />
                    <span className="settings-input-suffix">days before expiration</span>
                  </div>
                </div>

                <div className="settings-field">
                  <label htmlFor="reminder_time" className="settings-label">
                    Process Reminders At
                  </label>
                  <div className="settings-input-group">
                    <select
                      id="reminder_time"
                      name="reminder_time"
                      value={formData.reminder_time}
                      onChange={handleChange}
                      className="settings-select"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="settings-input-suffix">EST</span>
                  </div>
                  <p className="settings-help">
                    Daily check for sessions needing reminders
                  </p>
                </div>
              </>
            )}
          </section>

          {/* Notifications */}
          <section className="settings-section">
            <h2 className="settings-section-title">Notifications</h2>
            <p className="settings-section-description">
              Email addresses for system notifications
            </p>

            <div className="settings-field">
              <label htmlFor="admin_notification_email" className="settings-label">
                Admin Email
              </label>
              <input
                type="email"
                id="admin_notification_email"
                name="admin_notification_email"
                value={formData.admin_notification_email}
                onChange={handleChange}
                placeholder="proofs@seraphinagrey.com"
                className="settings-input"
              />
              <p className="settings-help">
                Leave blank to use system default from environment
              </p>
            </div>
          </section>

          {/* Business Info */}
          <section className="settings-section">
            <h2 className="settings-section-title">Business Info</h2>
            <p className="settings-section-description">
              Information displayed in customer emails
            </p>

            <div className="settings-field">
              <label htmlFor="business_name" className="settings-label">
                Business Name
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="settings-input"
              />
            </div>

            <div className="settings-field">
              <label htmlFor="support_email" className="settings-label">
                Support Email
              </label>
              <input
                type="email"
                id="support_email"
                name="support_email"
                value={formData.support_email}
                onChange={handleChange}
                className="settings-input"
              />
              <p className="settings-help">
                Displayed in emails as contact address
              </p>
            </div>
          </section>

          {/* Users Section - Admin only */}
          {canViewUsers && (
            <section className="settings-section users-section">
              <div className="settings-section-header">
                <div>
                  <h2 className="settings-section-title">Users</h2>
                  <p className="settings-section-description">
                    Manage admin users and permissions
                  </p>
                </div>
                {canCreateUsers && (
                  <button
                    type="button"
                    onClick={handleAddUser}
                    className="settings-btn settings-btn-primary settings-btn-small"
                  >
                    + Add User
                  </button>
                )}
              </div>

              {usersLoading ? (
                <div className="users-loading">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="users-empty">No users found</div>
              ) : (
                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          className={!user.is_active ? 'user-row-inactive' : ''}
                        >
                          <td className="user-cell-name">
                            <span className="user-name">
                              {user.display_name || user.username}
                            </span>
                            {user.display_name && (
                              <span className="user-username">@{user.username}</span>
                            )}
                            {user.id === currentUser?.id && (
                              <span className="user-you-badge">You</span>
                            )}
                          </td>
                          <td>
                            <span className={`role-badge ${getRoleColor(user.role)}`}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="user-cell-login">
                            {formatLastLogin(user.last_login)}
                          </td>
                          <td className="user-cell-actions">
                            {canEditUsers && (
                              <button
                                type="button"
                                onClick={() => handleEditUser(user)}
                                className="user-action-btn"
                                title="Edit user"
                              >
                                Edit
                              </button>
                            )}
                            {canDeleteUsers && user.username !== 'supervisor' && user.id !== currentUser?.id && (
                              user.is_active ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(user.id)}
                                  disabled={deletingUserId === user.id}
                                  className="user-action-btn user-action-btn-danger"
                                  title="Deactivate user"
                                >
                                  {deletingUserId === user.id ? '...' : 'Deactivate'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleReactivateUser(user.id)}
                                  className="user-action-btn user-action-btn-success"
                                  title="Reactivate user"
                                >
                                  Reactivate
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="users-footer">
                    {users.length} user{users.length !== 1 ? 's' : ''} total
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Storage Section */}
          <section className="settings-section">
            <h2 className="settings-section-title">Storage</h2>
            <p className="settings-section-description">
              Photo storage statistics and bucket management
            </p>

            {/* Load Stats Button */}
            {!storageStats && (
              <button
                type="button"
                onClick={loadStorageStats}
                disabled={storageLoading}
                className="settings-btn settings-btn-secondary"
              >
                {storageLoading ? 'Loading...' : 'Load Storage Stats'}
              </button>
            )}

            {/* Storage Stats Display */}
            {storageStats && (
              <div className="storage-stats-container">
                {/* Current Mode with Health Status */}
                <div className="storage-mode-indicator">
                  <span className="storage-mode-label">Current Storage Mode:</span>
                  <span className={`storage-mode storage-mode-${storageStats.currentMode}`}>
                    {storageStats.currentMode.toUpperCase()}
                  </span>
                  {storageStats.currentMode === 's3' && (
                    <span className={`storage-mode-status storage-mode-status-${storageStats.s3.s3Status}`}>
                      {storageStats.s3.s3Status === 'connected' && 'Connected'}
                      {storageStats.s3.s3Status === 'error' && 'Connection Failed'}
                      {storageStats.s3.s3Status === 'not_configured' && 'Not Configured'}
                    </span>
                  )}
                  {storageStats.currentMode === 'local' && (
                    <span className="storage-mode-status storage-mode-status-local">
                      Local disk storage
                    </span>
                  )}
                  {storageStats.s3Available && storageStats.currentMode === 'local' && (
                    <span className="storage-mode-hint">S3 Ready</span>
                  )}
                </div>

                {/* S3 Stats */}
                {storageStats.s3Available && (
                  <div className="storage-stats-card">
                    <div className="storage-card-header">
                      <div>
                        <h3>S3 Bucket: {storageStats.s3.bucketName}</h3>
                        <p className="storage-region">Region: {storageStats.s3.region}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openFileBrowser('s3')}
                        className="settings-btn settings-btn-secondary settings-btn-small"
                      >
                        View Files
                      </button>
                    </div>
                    <div className="storage-stats-grid">
                      <div className="storage-stat">
                        <span className="storage-stat-value">{formatBytes(storageStats.s3.totalSizeBytes)}</span>
                        <span className="storage-stat-label">Total Size</span>
                      </div>
                      <div className="storage-stat">
                        <span className="storage-stat-value">{storageStats.s3.photoCount}</span>
                        <span className="storage-stat-label">Photos</span>
                      </div>
                      <div className="storage-stat">
                        <span className="storage-stat-value">{storageStats.s3.thumbnailCount}</span>
                        <span className="storage-stat-label">Thumbnails</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Local Stats */}
                {storageStats.local.exists && (
                  <div className="storage-stats-card storage-stats-local">
                    <div className="storage-card-header">
                      <div>
                        <h3>Local Storage</h3>
                        <p className="storage-region">Path: {storageStats.local.uploadsDir}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openFileBrowser('local')}
                        className="settings-btn settings-btn-secondary settings-btn-small"
                      >
                        View Files
                      </button>
                    </div>
                    <div className="storage-stats-grid">
                      <div className="storage-stat">
                        <span className="storage-stat-value">{formatBytes(storageStats.local.totalSizeBytes)}</span>
                        <span className="storage-stat-label">Total Size</span>
                      </div>
                      <div className="storage-stat">
                        <span className="storage-stat-value">{storageStats.local.photoCount}</span>
                        <span className="storage-stat-label">Photos</span>
                      </div>
                      <div className="storage-stat">
                        <span className="storage-stat-value">{storageStats.local.thumbnailCount}</span>
                        <span className="storage-stat-label">Thumbnails</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Refresh button */}
                <button
                  type="button"
                  onClick={loadStorageStats}
                  disabled={storageLoading}
                  className="settings-btn settings-btn-secondary settings-btn-small"
                >
                  {storageLoading ? 'Refreshing...' : 'Refresh Stats'}
                </button>
              </div>
            )}

            {/* Bucket Audit Section */}
            {storageStats?.s3Available && (
              <div className="storage-audit-section">
                <h3 className="storage-audit-title">Bucket Audit</h3>
                <p className="settings-help">
                  Scan for orphaned files (in S3 but not in database) and ghost references
                </p>

                {!auditResult ? (
                  <button
                    type="button"
                    onClick={runStorageAudit}
                    disabled={auditLoading}
                    className="settings-btn settings-btn-secondary"
                  >
                    {auditLoading ? 'Scanning...' : 'Run Audit'}
                  </button>
                ) : (
                  <div className="audit-results">
                    <div className="audit-summary">
                      <div className="audit-stat">
                        <span className="audit-stat-value">{auditResult.totalS3Objects}</span>
                        <span className="audit-stat-label">S3 Objects</span>
                      </div>
                      <div className="audit-stat">
                        <span className="audit-stat-value">{auditResult.totalDbRecords}</span>
                        <span className="audit-stat-label">DB Records</span>
                      </div>
                      <div className={`audit-stat ${auditResult.orphanedFiles.length > 0 ? 'audit-stat-warning' : ''}`}>
                        <span className="audit-stat-value">{auditResult.orphanedFiles.length}</span>
                        <span className="audit-stat-label">Orphaned Files</span>
                      </div>
                      <div className={`audit-stat ${auditResult.ghostReferences.length > 0 ? 'audit-stat-error' : ''}`}>
                        <span className="audit-stat-value">{auditResult.ghostReferences.length}</span>
                        <span className="audit-stat-label">Ghost References</span>
                      </div>
                    </div>

                    {auditResult.orphanedFiles.length > 0 && (
                      <div className="audit-orphans">
                        <p className="audit-orphans-info">
                          {auditResult.orphanedFiles.length} orphaned files found ({auditResult.orphanedSizeMB} MB recoverable)
                        </p>
                        <div className="audit-orphans-list">
                          {auditResult.orphanedFiles.slice(0, 5).map((file) => (
                            <div key={file.key} className="audit-orphan-item">
                              <span className="audit-orphan-session" title={`Session: ${file.sessionId}`}>
                                {file.sessionId.slice(0, 8)}
                              </span>
                              <span className="audit-orphan-type">{file.type}</span>
                              <span className="audit-orphan-size">{formatBytes(file.sizeBytes)}</span>
                            </div>
                          ))}
                          {auditResult.orphanedFiles.length > 5 && (
                            <p className="audit-orphans-more">
                              ...and {auditResult.orphanedFiles.length - 5} more
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={deleteOrphanedFiles}
                          disabled={deletingOrphans}
                          className="settings-btn settings-btn-danger"
                        >
                          {deletingOrphans ? 'Deleting...' : 'Delete Orphaned Files'}
                        </button>
                      </div>
                    )}

                    {auditResult.ghostReferences.length > 0 && (
                      <div className="audit-ghosts">
                        <p className="audit-ghosts-info">
                          {auditResult.ghostReferences.length} ghost references (DB records pointing to missing files)
                        </p>
                        <p className="settings-help">
                          These need manual investigation - the database references files that don't exist in S3.
                        </p>
                      </div>
                    )}

                    {auditResult.orphanedFiles.length === 0 && auditResult.ghostReferences.length === 0 && (
                      <p className="audit-clean">All files are properly linked. No orphans or ghosts found.</p>
                    )}

                    <div className="audit-actions">
                      <button
                        type="button"
                        onClick={runStorageAudit}
                        disabled={auditLoading}
                        className="settings-btn settings-btn-secondary settings-btn-small"
                      >
                        {auditLoading ? 'Scanning...' : 'Re-run Audit'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuditResult(null)}
                        className="settings-btn settings-btn-secondary settings-btn-small"
                      >
                        Clear Results
                      </button>
                    </div>
                  </div>
                )}

                {/* Danger Zone */}
                <div className="storage-danger-zone">
                  <h3 className="storage-audit-title danger-title">Danger Zone</h3>
                  <p className="settings-help">
                    Destructive actions that cannot be undone
                  </p>
                  <div className="danger-zone-buttons">
                    <button
                      type="button"
                      onClick={() => setShowResetModal(true)}
                      className="settings-btn settings-btn-danger"
                    >
                      Reset Bucket
                    </button>
                    {storageStats.local.exists && (
                      <button
                        type="button"
                        onClick={() => setShowLocalResetModal(true)}
                        className="settings-btn settings-btn-danger"
                      >
                        Reset Local Storage
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="settings-actions">
            <button
              type="button"
              onClick={handleReset}
              disabled={!hasChanges || saving}
              className="settings-btn settings-btn-secondary"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={!hasChanges || saving}
              className="settings-btn settings-btn-primary"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>

        {/* Bucket Reset Confirmation Modal */}
        {showResetModal && storageStats?.s3.bucketName && (
          <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
            <div className="modal-content modal-danger" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Reset S3 Bucket</h2>

              <div className="modal-warning">
                <strong>This action is DESTRUCTIVE and cannot be undone.</strong>
                <p>
                  All photos and thumbnails in the S3 bucket will be permanently deleted.
                  This includes all proof session images.
                </p>
              </div>

              <div className="modal-warning modal-warning-db">
                <strong>Have you backed up your database?</strong>
                <p>
                  After resetting the bucket, database records will reference files that no longer exist.
                  You may want to reset your database as well using: <code>cd backend && npm run db:reset</code>
                </p>
              </div>

              <div className="modal-field">
                <label htmlFor="reset-confirmation" className="settings-label">
                  Type the bucket name to confirm: <strong>{storageStats.s3.bucketName}</strong>
                </label>
                <input
                  type="text"
                  id="reset-confirmation"
                  value={resetConfirmation}
                  onChange={(e) => setResetConfirmation(e.target.value)}
                  placeholder={storageStats.s3.bucketName}
                  className="settings-input"
                  autoComplete="off"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setResetConfirmation('');
                  }}
                  className="settings-btn settings-btn-secondary"
                  disabled={resettingBucket}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetBucket}
                  disabled={resetConfirmation !== storageStats.s3.bucketName || resettingBucket}
                  className="settings-btn settings-btn-danger"
                >
                  {resettingBucket ? 'Resetting...' : 'Reset Bucket'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* File Browser Modal */}
        {showFileBrowser && (
          <div className="modal-overlay" onClick={() => setShowFileBrowser(false)}>
            <div className="modal-content file-browser-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {fileBrowserSource === 's3' ? 'S3 Files' : 'Local Files'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowFileBrowser(false)}
                  className="modal-close-btn"
                >
                  &times;
                </button>
              </div>

              {filesLoading ? (
                <div className="file-browser-loading">
                  <div className="loading-spinner" />
                  <p>Loading files...</p>
                </div>
              ) : storageFiles.length === 0 ? (
                <div className="file-browser-empty">
                  <p>No files found in {fileBrowserSource === 's3' ? 'S3 bucket' : 'local storage'}</p>
                </div>
              ) : (
                <div className="file-browser-content">
                  <div className="file-browser-header-row">
                    <span>Preview</span>
                    <span>Session ID</span>
                    <span>Type</span>
                    <span>Size</span>
                  </div>
                  <div className="file-browser-list">
                    {storageFiles.map((file) => (
                      <div key={file.key} className="file-browser-row">
                        <img
                          src={file.thumbnailUrl}
                          alt=""
                          className="file-browser-thumb"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="file-browser-session" title={`Full ID: ${file.sessionId}`}>
                          {file.sessionId.slice(0, 8)}
                        </span>
                        <span className="file-browser-type">{file.type}</span>
                        <span className="file-browser-size">{formatBytes(file.sizeBytes)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="file-browser-footer">
                    <span>{storageFiles.length} files</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Local Storage Reset Confirmation Modal */}
        {showLocalResetModal && (
          <div className="modal-overlay" onClick={() => setShowLocalResetModal(false)}>
            <div className="modal-content modal-danger" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Reset Local Storage</h2>

              <div className="modal-warning">
                <strong>This action is DESTRUCTIVE and cannot be undone.</strong>
                <p>
                  All photos and thumbnails in local storage will be permanently deleted.
                  This includes all proof session images stored locally.
                </p>
              </div>

              <div className="modal-warning modal-warning-db">
                <strong>Have you backed up your database?</strong>
                <p>
                  After resetting local storage, database records may reference files that no longer exist.
                  You may want to reset your database as well using: <code>cd backend && npm run db:reset</code>
                </p>
              </div>

              <div className="modal-field">
                <label htmlFor="local-reset-confirmation" className="settings-label">
                  Type <strong>DELETE LOCAL</strong> to confirm:
                </label>
                <input
                  type="text"
                  id="local-reset-confirmation"
                  value={localResetConfirmation}
                  onChange={(e) => setLocalResetConfirmation(e.target.value)}
                  placeholder="DELETE LOCAL"
                  className="settings-input"
                  autoComplete="off"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowLocalResetModal(false);
                    setLocalResetConfirmation('');
                  }}
                  className="settings-btn settings-btn-secondary"
                  disabled={resettingLocal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetLocal}
                  disabled={localResetConfirmation !== 'DELETE LOCAL' || resettingLocal}
                  className="settings-btn settings-btn-danger"
                >
                  {resettingLocal ? 'Resetting...' : 'Reset Local Storage'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Modal */}
        {showUserModal && currentUser && (
          <UserModal
            user={editingUser}
            onClose={() => {
              setShowUserModal(false);
              setEditingUser(null);
            }}
            onSave={handleUserSaved}
            currentUserId={currentUser.id}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default Settings;
